import { DEFAULT_BOX_PROGRAMMATION_COLOR, getBoxProgramColorTypeWithHex } from "@/modules/boxProgrammation/boxProgramUtils"
import type { BoxProgramColorType } from "@/modules/boxProgrammation/components/boxProgramColors"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import { word } from "@modules/core/utils/i18n"
import { z } from "zod"

export type CoachCategoryInfoFormFields = {
  name: string
  description: string
  color: BoxProgramColorType
  offerIds: string[]
  visibleToClients: boolean
}

export type CoachCategoryDefaultSettingsFormFields = {
  duration: number
  participantLimit: number | null
  trialsAccepted: boolean
  limitStart?: number
  limitEnd?: number
  limitCancel?: number
  location: string | null
}

export const getCoachCategoryInfoSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, word("global.form.required"))
      .max(64, word("form.maximum.characters", { count: 64 })),
    description: z.string().trim().min(1, word("global.form.required")),
    color: z.custom<BoxProgramColorType>(),
    offerIds: z.array(z.string()),
    visibleToClients: z.boolean(),
  })

export const getCoachCategoryDefaultSettingsSchema = () =>
  z
    .object({
      duration: z.number().min(1, word("global.form.minimumValue", { minValue: 1 })),
      participantLimit: z
        .number({ message: word("global.form.required") })
        .min(
          BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
          word("global.form.minimumValue", { minValue: BOX_SLOT_PARTICIPANTS_LIMIT_MIN }),
        )
        .max(
          BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
          word("global.form.maximumValue", { maxValue: BOX_SLOT_PARTICIPANTS_LIMIT_MAX }),
        ),
      trialsAccepted: z.boolean(),
      limitStart: z
        .number({ message: word("global.form.required") })
        .min(1, word("global.form.minimumValue", { minValue: 1 })),
      limitEnd: z
        .number({ message: word("global.form.required") })
        .min(1, word("global.form.minimumValue", { minValue: 1 })),
      limitCancel: z
        .number({ message: word("global.form.required") })
        .min(1, word("global.form.minimumValue", { minValue: 1 })),
      location: z
        .string()
        .trim()
        .max(40, word("form.maximum.characters", { count: 40 }))
        .nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.limitStart <= data.limitEnd) {
        ctx.addIssue({
          code: "custom",
          message: word("box.slotTemplateGroup.form.registration.end_error"),
          path: ["limitEnd"],
        })
      }
    })

export const getCoachCategoryInfoDraftValues = (): CoachCategoryInfoFormFields => ({
  name: "",
  description: "",
  color: getBoxProgramColorTypeWithHex(DEFAULT_BOX_PROGRAMMATION_COLOR),
  offerIds: [],
  visibleToClients: true,
})

export const getCoachCategoryDefaultSettingsDraftValues = (): CoachCategoryDefaultSettingsFormFields => ({
  duration: 60,
  participantLimit: null,
  trialsAccepted: true,
  limitStart: undefined,
  limitEnd: undefined,
  limitCancel: undefined,
  location: null,
})
